import { ComponentType, useMemo } from 'react';
import { useForgotPasswordStore } from '@shared/store/useForgotPasswordStore';
import {
  createForgotPasswordFormSubmitHandler,
  getForgotPasswordFormCopy,
} from '@modules/forgot-password/business_logic';
import { useForgotPasswordRequest } from '../hooks/useForgotPasswordRequest';
import { useForgotPasswordVerify } from '../hooks/useForgotPasswordVerify';
import type { ForgotPasswordProps } from '../views/forgot_password_view';

interface ForgotPasswordContainerProps {
  View: ComponentType<ForgotPasswordProps>;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  onSuccessNavigate: () => void;
  onNavigateToLogin: () => void;
  useFormSubmitHandler?: boolean;
}

export function ForgotPasswordContainer({
  View,
  onSuccess,
  onError,
  onSuccessNavigate,
  onNavigateToLogin,
  useFormSubmitHandler = false,
}: ForgotPasswordContainerProps) {
  const step = useForgotPasswordStore((s) => s.step);
  const isSubmitting = useForgotPasswordStore((s) => s.isSubmitting);
  const form = useForgotPasswordStore((s) => s.form);
  const setForm = useForgotPasswordStore((s) => s.setForm);
  const reset = useForgotPasswordStore((s) => s.reset);

  const handleRequest = useForgotPasswordRequest(onSuccess, onError);
  const handleVerify = useForgotPasswordVerify(onSuccess, onError, onSuccessNavigate);

  const handleSubmit = step === 'request' ? handleRequest : handleVerify;

  const handleCancel = () => {
    if (step === 'verify') {
      reset();
      return;
    }
    onNavigateToLogin();
  };

  const formCopy = useMemo(
    () => getForgotPasswordFormCopy(step, isSubmitting),
    [step, isSubmitting]
  );

  const onFormSubmit = useMemo(
    () => (useFormSubmitHandler ? createForgotPasswordFormSubmitHandler(handleSubmit) : undefined),
    [useFormSubmitHandler, handleSubmit]
  );

  return (
    <View
      {...formCopy}
      step={step}
      isSubmitting={isSubmitting}
      email={form.email}
      code={form.code}
      newPassword={form.newPassword}
      onEmailChange={(email) => setForm({ email })}
      onCodeChange={(code) => setForm({ code })}
      onNewPasswordChange={(newPassword) => setForm({ newPassword })}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      onFormSubmit={onFormSubmit}
    />
  );
}

export type { ForgotPasswordProps };
